import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useGameContext } from '../contexts/GameContext';
import { useGameFlow } from '../hooks/useGameFlow';
import { announce, prefersReducedMotion } from '../utils/a11y';
import { parseExecutiveSummary } from '../utils/executiveSummaryParser';
import { GameScenarioContent } from '../types/ScenarioManagement';
import './CEOScenarioScreen.css';

interface CEOScenarioScreenProps {
  onBeginSimulation?: () => void;
}

const CEOScenarioScreen: React.FC<CEOScenarioScreenProps> = ({ onBeginSimulation }) => {
  const { gameState, setPlayerInfo } = useGameContext();
  const { navigateToInvestments } = useGameFlow();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const shouldReduceMotion = prefersReducedMotion();

  // Parse executive summary if available
  const briefingContent = React.useMemo(() => {
    const selectedScenario = gameState.selectedScenario;
    if (selectedScenario && selectedScenario.content) {
      const gameContent = selectedScenario.content as GameScenarioContent;
      if (gameContent.executiveSummary) {
        return parseExecutiveSummary(gameContent.executiveSummary);
      }
    }
    // Default content if no scenario data
    return {
      role: `As Chief Executive Officer of TechCorp International, you lead a Fortune 500 technology company with operations spanning 45 countries and annual revenues exceeding $12 billion. Your leadership has driven consistent growth over the past five years, establishing TechCorp as an industry leader in cloud computing and enterprise software solutions.`,
      businessContext: `The Board of Directors has set ambitious targets for the next fiscal year:`,
      businessObjectives: [
        'Geographic expansion into emerging markets in Southeast Asia and Latin America',
        'Integration of advanced AI capabilities across all product lines',
        '20% revenue growth while maintaining current profit margins',
        'Digital transformation of legacy infrastructure systems'
      ],
      challenge: `Balance aggressive growth objectives with prudent risk management. Every decision you make will impact your company's security posture, financial performance, and stakeholder confidence. The Board expects you to deliver growth while protecting the organization from potentially catastrophic cyber incidents.\n\nThis simulation will test your ability to navigate complex risk scenarios, make strategic decisions under pressure, and allocate resources effectively to protect your organization's assets and reputation.`
    };
  }, [gameState.selectedScenario]);

  // Get cybersecurity landscape from scenario data
  const cybersecurityLandscape = React.useMemo(() => {
    const selectedScenario = gameState.selectedScenario;
    if (selectedScenario && selectedScenario.content) {
      const gameContent = selectedScenario.content as GameScenarioContent;
      if (gameContent.briefing && gameContent.briefing.cybersecurityLandscape) {
        return gameContent.briefing.cybersecurityLandscape;
      }
    }
    // Default cybersecurity landscape if no scenario data
    return {
      title: "Cybersecurity Landscape",
      description: "Recent intelligence reports indicate heightened cyber threat activity targeting technology sector leaders. Your Chief Information Security Officer has briefed you on several concerning developments:",
      threats: [
        {
          id: "nation-state-actors",
          title: "Nation-State Actors",
          description: "Advanced persistent threats targeting intellectual property and strategic plans"
        },
        {
          id: "ransomware-syndicates",
          title: "Ransomware Syndicates", 
          description: "Sophisticated criminal organizations with average demands exceeding $5 million"
        },
        {
          id: "supply-chain-vulnerabilities",
          title: "Supply Chain Vulnerabilities",
          description: "Third-party risks from vendors and partners in your expanding ecosystem"
        },
        {
          id: "insider-threats",
          title: "Insider Threats",
          description: "Risks from employee turnover and remote workforce management"
        }
      ]
    };
  }, [gameState.selectedScenario]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: shouldReduceMotion ? { duration: 0 } : {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: shouldReduceMotion ? { duration: 0 } : {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const threatItemVariants = {
    hidden: { opacity: 0, scale: shouldReduceMotion ? 1 : 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: shouldReduceMotion ? { duration: 0 } : {
        duration: 0.5,
      },
    },
  };

  // Scroll to top on mount without interfering with focus
  useEffect(() => {
    // Scroll to top of page
    window.scrollTo(0, 0);
  }, []);


  const handleStartSimulation = () => {
    const companyName = gameState.selectedScenario?.title || 'TechCorp International';
    setPlayerInfo('Participant', companyName);

    // Check if we're skipping investments
    const gameContent = gameState.selectedScenario?.content as GameScenarioContent;
    const skipInvestments = gameContent?.gameSettings?.skipInvestments;
    const nextStep = skipInvestments ? 'incident response' : 'investment selection';
    announce(`Proceeding to ${nextStep}.`, 'polite');

    if (onBeginSimulation) {
      onBeginSimulation();
    } else {
      navigateToInvestments();
    }
  };

  return (
    <motion.div 
      className="ceo-scenario-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      role="region"
      aria-label="CEO Scenario Introduction"
    >
      <div className="ceo-scenario-content">
        <motion.div className="executive-briefing" variants={itemVariants}>
          <h1 className="briefing-title" ref={headingRef} id="briefing-title">Executive Briefing</h1>
          <div className="briefing-subtitle" role="doc-subtitle">Strategic Risk Management Simulation</div>
        </motion.div>

        <motion.section className="scenario-section" variants={itemVariants} aria-labelledby="role-heading">
          <h2 className="section-title" id="role-heading">Your Role</h2>
          <p className="scenario-text">
            {briefingContent.role}
          </p>
        </motion.section>

        {(briefingContent.businessContext || briefingContent.businessObjectives.length > 0) && (
          <motion.section className="scenario-section" variants={itemVariants} aria-labelledby="context-heading">
            <h2 className="section-title" id="context-heading">Current Business Context</h2>
            {briefingContent.businessContext && (
              <p className="scenario-text">
                {briefingContent.businessContext}
              </p>
            )}
            {briefingContent.businessObjectives.length > 0 && (
              <>
                <p className="scenario-text" style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Strategic Objectives:</p>
                <motion.ul className="business-objectives" variants={containerVariants} role="list" aria-label="Strategic business objectives">
                  {briefingContent.businessObjectives.map((objective, index) => (
                    <motion.li key={index} variants={itemVariants}>{objective}</motion.li>
                  ))}
                </motion.ul>
                <p className="scenario-text">
                  These strategic initiatives require significant technology investments and expanded digital
                  infrastructure, substantially increasing your organization's cyber risk exposure.
                </p>
              </>
            )}
          </motion.section>
        )}

        <motion.section className="scenario-section" variants={itemVariants} aria-labelledby="cybersecurity-heading">
          <h2 className="section-title" id="cybersecurity-heading">{cybersecurityLandscape.title}</h2>
          <p className="scenario-text">
            {cybersecurityLandscape.description}
          </p>
          <motion.div className="threat-landscape" variants={containerVariants} role="list" aria-label="Current cybersecurity threats">
            {cybersecurityLandscape.threats?.map((threat) => (
              <motion.div key={threat.id} className="threat-item" variants={threatItemVariants} role="listitem">
                <h3>{threat.title}</h3>
                <p>{threat.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        <motion.section className="scenario-section" variants={itemVariants} aria-labelledby="challenge-heading">
          <h2 className="section-title" id="challenge-heading">Your Challenge</h2>
          <div className="scenario-text">
            {briefingContent.challenge.split('\n\n').map((paragraph, index) => (
              <p key={index} className={index === 0 ? 'emphasis' : ''}>
                {paragraph}
              </p>
            ))}
          </div>
        </motion.section>

        <motion.div
          className="action-section"
          variants={itemVariants}
          role="region"
          aria-label="Begin simulation action"
        >
          <button
            className="begin-button"
            type="button"
            onClick={handleStartSimulation}
            aria-label="Start scenario"
          >
            Start Scenario
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CEOScenarioScreen;